/**
 * Dispatchers API Service
 *
 * API service for dispatchers management operations
 */
import { apiClient } from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'
import authService from './authService'
import type { AxiosResponse } from 'axios'
import type {
  CreateDispatcherPayload,
  DispatcherResponse,
  UpdateDispatcherActivePrecinctPayload,
  UpdateDispatcherProfilePayload,
} from '@/types/api/dispatcher'

class DispatcherService {
  /**
   * Create dispatcher record (e.g. after first-time password set / registration).
   * POST /dispatchers with the given payload.
   */
  async createDispatcher(payload: CreateDispatcherPayload): Promise<any> {
    try {
      const response: AxiosResponse<any> = await apiClient.post(
        API_ENDPOINTS.DISPATCHERS,
        payload,
        { validateStatus: () => true }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'createDispatcher')
    }
  }

  /**
   * Get all dispatchers
   */
  async getAllDispatchers(): Promise<any> {
    try {
      const precinctInfo = await authService.getPrecinct()

      const queryParams: Record<string, string | number> = {}

      if (precinctInfo?.precinct_id) {
        queryParams.precinct_id = precinctInfo.precinct_id
      }

      const response: AxiosResponse<any> = await apiClient.get(
        API_ENDPOINTS.DISPATCHERS,
        {
          params: queryParams,
          validateStatus: () => true,
        }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'getAllDispatchers')
    }
  }

  /**
   * Get dispatcher by ID
   */
  async getDispatcher(dispatcherId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        API_ENDPOINTS.DISPATCHER_BY_ID(dispatcherId)
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'getDispatcher')
    }
  }

  /**
   * Update profile fields for a dispatcher
   */
  async updateDispatcher(
    dispatcherId: string,
    payload: UpdateDispatcherProfilePayload
  ): Promise<DispatcherResponse> {
    try {
      const response: AxiosResponse<DispatcherResponse> = await apiClient.put(
        API_ENDPOINTS.DISPATCHER_BY_ID(dispatcherId),
        payload
      )

      return response.data
    } catch (error) {
      throw handleApiError(error, 'updateDispatcher')
    }
  }

  /**
   * Switch the authenticated dispatcher's active precinct
   */
  async updateActivePrecinct(
    payload: UpdateDispatcherActivePrecinctPayload
  ): Promise<DispatcherResponse> {
    try {
      const response: AxiosResponse<DispatcherResponse> = await apiClient.put(
        API_ENDPOINTS.DISPATCHER_ACTIVE_PRECINCT,
        payload
      )

      return response.data
    } catch (error) {
      throw handleApiError(error, 'updateActivePrecinct')
    }
  }
}

// Export singleton instance
export const dispatcherService = new DispatcherService()
export default dispatcherService
